import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Tenant } from '../../services/api/tenantService';
import {
  fetchTenants,
  fetchTenant,
  fetchUserTenants,
  createTenant,
  updateTenant,
  deleteTenant,
} from '../thunks/tenantThunks';
import { logoutSuccess, logoutFailure, accountSwitchSuccess, loginSuccess } from './authSlice';

const ACTIVE_TENANT_KEY = 'sm_active_tenant';
const PICKER_DISMISSED_KEY = 'sm_tenant_picker_dismissed';

export interface ActiveTenant {
  tenantId: string;
  tenantCode: string;
  tenantName: string;
  /** Raw JSON string from Tenant.ConfigurationJson — used for workspace branding. */
  configurationJson?: string | null;
}

function loadActiveTenantFromStorage(): ActiveTenant | null {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(ACTIVE_TENANT_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.tenantId === 'string' && typeof parsed.tenantCode === 'string') {
      return parsed as ActiveTenant;
    }
    return null;
  } catch {
    return null;
  }
}

export interface TenantState {
  tenants: Tenant[];
  /** Tenants scoped to the current user — populated by fetchUserTenants, used by TenantPickerModal */
  userTenants: Tenant[];
  selectedTenant: Tenant | null;
  isLoading: boolean;
  error: string | null;
  activeTenant: ActiveTenant | null;
  /** Number of tenants assigned to the current user — used by nav bar to decide visibility */
  userTenantCount: number;
  /** True if ANY of the user's tenants has workspace.force_home enabled (strictest-wins) */
  forceWorkspaceHome: boolean;
  /** True once fetchUserTenants has completed (distinguishes "not loaded" from "loaded but empty") */
  userTenantsLoaded: boolean;
}

const initialState: TenantState = {
  tenants: [],
  userTenants: [],
  selectedTenant: null,
  isLoading: false,
  error: null,
  activeTenant: loadActiveTenantFromStorage(),
  userTenantCount: 0,
  forceWorkspaceHome: false,
  userTenantsLoaded: false,
};

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    clearSelectedTenant: (state) => {
      state.selectedTenant = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setActiveTenant: (state, action: PayloadAction<ActiveTenant | null>) => {
      state.activeTenant = action.payload;
      try {
        if (action.payload) {
          localStorage.setItem(ACTIVE_TENANT_KEY, JSON.stringify(action.payload));
          // User has made a tenant choice — clear the "dismissed" flag so the
          // picker can show again in future sessions where it's appropriate.
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem(PICKER_DISMISSED_KEY);
          }
        } else {
          localStorage.removeItem(ACTIVE_TENANT_KEY);
        }
      } catch {
        // localStorage unavailable (SSR or private browsing) — state still updated
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenants.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tenants = action.payload;
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchTenant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTenant.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedTenant = action.payload;
      })
      .addCase(fetchTenant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createTenant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTenant.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createTenant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateTenant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTenant.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateTenant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteTenant.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteTenant.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tenants = state.tenants.filter((t) => t.id !== action.payload);
      })
      .addCase(deleteTenant.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchUserTenants.fulfilled, (state, action) => {
        state.userTenants = action.payload.tenants;
        state.userTenantCount = action.payload.tenants.length;
        state.forceWorkspaceHome = action.payload.forceWorkspaceHome;
        state.userTenantsLoaded = true;
      })
      .addCase(fetchUserTenants.rejected, (state) => {
        // Unblock guards even on failure — prevents permanent spinner
        state.userTenantsLoaded = true;
      });

    // Clear all tenant state on logout
    const clearTenantState = (state: TenantState) => {
      state.activeTenant = null;
      state.userTenants = [];
      state.userTenantCount = 0;
      state.forceWorkspaceHome = false;
      state.userTenantsLoaded = false;
      state.tenants = [];
      state.selectedTenant = null;
      state.error = null;
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(ACTIVE_TENANT_KEY);
          // Reset picker dismissal so the new account / fresh login can see it.
          sessionStorage.removeItem(PICKER_DISMISSED_KEY);
        }
      } catch {
        // localStorage unavailable — state still updated
      }
    };
    builder.addCase(logoutSuccess, clearTenantState);
    builder.addCase(logoutFailure, clearTenantState);
    // Clear tenant state on login/account switch so TenantLoader re-fetches for the new user.
    // loginSuccess covers Google OAuth add-account which fires loginSuccess (not accountSwitchSuccess).
    builder.addCase(loginSuccess, clearTenantState);
    builder.addCase(accountSwitchSuccess, clearTenantState);
  },
});

export const { clearSelectedTenant, clearError, setActiveTenant } = tenantSlice.actions;
export default tenantSlice.reducer;
